import { FC, PropsWithChildren, useRef } from 'react'
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { alpha, Box, styled } from '@mui/material'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { useSnapshot } from 'valtio'

import { ScreenMaskState } from '@app/storage/screenMask'
import { ScreenMaskArea } from '@app/components/common/ScreenMaskArea'

export const AssessmentItemWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { opened } = useSnapshot(ScreenMaskState)
  const wrapperRef = useRef(null)

  const sensors = useSensors(useSensor(MouseSensor), useSensor(KeyboardSensor), useSensor(TouchSensor))

  const getWrapperElement = () => wrapperRef.current

  return (
    <>
      {opened ? (
        <Wrapper component='main' aria-label='Question area' ref={wrapperRef} tabIndex={-1}>
          <DndContext modifiers={[restrictToParentElement]} sensors={sensors}>
            {children}
            <ScreenMaskArea getWrapperElement={getWrapperElement} />
          </DndContext>
        </Wrapper>
      ) : (
        <Wrapper component='main' aria-label='Question area' tabIndex={-1}>
          {children}
        </Wrapper>
      )}
    </>
  )
}

export const Wrapper = styled(Box)(
  ({ theme }) => `
  position: relative;
  padding: ${theme.spacing(4, 8)};
  display: flex;
  justify-content: center;
  gap: ${theme.spacing(6)};
  overflow: hidden;
  height: 100%;
  background-color: ${alpha(theme.palette.grey[50], 0.4)};

  &:focus {
    outline: none;
  }

  ${[theme.breakpoints.down('tablet')]} {
    /* a11y: release the fixed 100vh frame so the page scrolls as one column at high
       zoom instead of trapping content in nested panes (SC 1.4.10) */
    overflow: visible;
    height: auto;
  }

  ${[theme.breakpoints.down('mobile')]} {
    padding: ${theme.spacing(3)};
    flex-direction: column;
    gap: ${theme.spacing(3)};
  }
`,
)
